import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ServiceInventoryItem, ServiceInventoryState, ServiceState } from './service-inventory.model';
import {
  deleteServiceState,
  loadServiceInventory,
  loadServiceInventoryFailure,
  loadServiceInventorySuccess,
  updateServiceState,
} from './service-inventory.actions';
import { selectAllServices, selectServiceByKey } from './service-inventory.selectors';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

export type LoadInventoryResult =
  | { success: true; items: ServiceInventoryItem[] }
  | { success: false; error: string };

@Injectable({ providedIn: 'root' })
export class ServiceInventoryService {
  private _store = inject(Store<ServiceInventoryState>);
  private _actions$ = inject(Actions);

  /** Dispatch an action to load the service inventory. Returns an observable that emits once when the load succeeds or fails. */
  loadInventory(): Observable<LoadInventoryResult> {
    this._store.dispatch(loadServiceInventory());
    return this._actions$.pipe(
      ofType(loadServiceInventorySuccess, loadServiceInventoryFailure),
      take(1),
      map((action) =>
        action.type === loadServiceInventorySuccess.type
          ? { success: true as const, items: action.items }
          : { success: false as const, error: action.error }
      )
    );
  }

  /** Get all services as an observable */
  getInventory(): Observable<any> {
    return this._store.select(selectAllServices);
  }

  /** Get a specific service by key */
  getService(key: string): Observable<ServiceInventoryItem> {
    return this._store.select(selectServiceByKey(key));
  }

  /** Update the state of a specific service */
  updateServiceState(key: string, newState: ServiceState) {
    this._store.dispatch(updateServiceState({ key, state: newState }));
  }

  /** Delete the state of a specific service */
  deleteServiceState(key: string) {
    this._store.dispatch(deleteServiceState({ key }));
  }
}
